import { TextField } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  canonicalFromMinorDigits,
  formatMinorDigits,
  getNumericConfig,
  minorDigitsFromValue,
} from "../utilities/numericInput";
import { moveCaretToEnd } from "../utilities/helper";

const FixedDecimalTextField = ({
  component,
  value,
  onValueChange,
  onCommit,
  stopPropagation = true,
  InputProps,
  inputProps,
  sx,
  onFocus,
  onBlur,
  onKeyDown,
  onMouseUp,
  onSelect,
  ...textFieldProps
}) => {
  const inputRef = useRef(null);
  const replaceOnNextDigit = useRef(false);
  const config = useMemo(
    () => getNumericConfig(component),
    [
      component?.iDecimalPlaces,
      component?.sDecimalChar,
      component?.sThousandSeparator,
      component?.sJustify,
    ],
  );
  const [minorDigits, setMinorDigits] = useState(() =>
    minorDigitsFromValue(value, config),
  );
  const minorDigitsRef = useRef(minorDigits);

  useEffect(() => {
    const nextMinorDigits = minorDigitsFromValue(value, config);
    minorDigitsRef.current = nextMinorDigits;
    setMinorDigits(nextMinorDigits);
  }, [value, config]);

  const publishDigits = nextDigits => {
    const normalizedDigits = String(nextDigits || "0").replace(/^0+(?=\d)/, "") || "0";
    const canonicalValue = canonicalFromMinorDigits(normalizedDigits, config);
    const displayValue = formatMinorDigits(normalizedDigits, config);

    minorDigitsRef.current = normalizedDigits;
    setMinorDigits(normalizedDigits);
    onValueChange?.(canonicalValue, displayValue);

    requestAnimationFrame(() => moveCaretToEnd(inputRef.current));
  };

  const handleKeyDown = event => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;

    if (stopPropagation) event.stopPropagation();
    if (event.ctrlKey || event.metaKey || event.altKey) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "a") {
        replaceOnNextDigit.current = true;
      }
      return;
    }

    const hasSelection = event.currentTarget.selectionStart !== event.currentTarget.selectionEnd;

    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      publishDigits(
        `${replaceOnNextDigit.current ? "" : minorDigitsRef.current}${event.key}`,
      );
      replaceOnNextDigit.current = false;
      return;
    }

    if (event.key === "Backspace") {
      event.preventDefault();
      publishDigits(hasSelection ? "0" : minorDigitsRef.current.slice(0, -1));
      return;
    }

    if (event.key === "Delete") {
      event.preventDefault();
      publishDigits("0");
      return;
    }

    if (event.key === "Enter") {
      onCommit?.(
        canonicalFromMinorDigits(minorDigitsRef.current, config),
        formatMinorDigits(minorDigitsRef.current, config),
      );
      event.currentTarget.blur();
    }
  };

  const handleChange = event => {
    // Keyboard input is handled above. This path supports mobile keyboards,
    // browser autofill, and paste while preserving fixed-decimal semantics.
    publishDigits(event.target.value.replace(/\D/g, ""));
  };

  const handleFocus = event => {
    replaceOnNextDigit.current = true;
    event.target.select();
    onFocus?.(event);
  };

  const handleBlur = event => {
    const canonicalValue = canonicalFromMinorDigits(minorDigitsRef.current, config);
    const displayValue = formatMinorDigits(minorDigitsRef.current, config);
    onCommit?.(canonicalValue, displayValue);
    onBlur?.(event);
  };

  const handleMouseUp = event => {
    onMouseUp?.(event);
  };

  const handleSelect = event => {
    replaceOnNextDigit.current =
      event.currentTarget.selectionStart !== event.currentTarget.selectionEnd;
    inputProps?.onSelect?.(event);
    onSelect?.(event);
  };

  return (
    <TextField
      {...textFieldProps}
      type="text"
      inputRef={inputRef}
      value={formatMinorDigits(minorDigits, config)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onMouseUp={handleMouseUp}
      inputProps={{
        ...inputProps,
        inputMode: "numeric",
        onSelect: handleSelect,
        style: {
          ...inputProps?.style,
          textAlign: config.justify,
          MozAppearance: "textfield",
        },
      }}
      InputProps={InputProps}
      sx={[
        {
          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

export default FixedDecimalTextField;
